import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, FileText, Accessibility as AccessIcon, ChevronRight, Bell, ChevronLeft } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { DemoBadge } from "@/components/Badges";
import api from "@/lib/api";
import { profileChip, DISCLAIMER_SHORT } from "@/lib/constants";

export default function Home() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([api.get("/profile"), api.get("/trips")]);
        setProfile(p.data);
        setTrips(t.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chip = profileChip(profile);

  const reminder = trips
    .filter((t) => t.days_until_start != null && t.days_until_start >= 0 && t.days_until_start <= 14)
    .sort((a, b) => a.days_until_start - b.days_until_start)[0];

  const pct = (t) =>
    t.checklist_total ? Math.round((t.checklist_done / t.checklist_total) * 100) : 0;

  return (
    <AppShell
      active="home"
      title="Lifestyle"
      headerRight={
        <button
          data-testid="switch-to-care"
          aria-label="Back to Care"
          onClick={() => navigate("/care")}
          className="bg-[#222222] border border-[#333333] rounded-full px-3 py-2 flex items-center gap-1.5 text-xs font-bold text-[#F8FAFC] transition-transform active:scale-95"
        >
          <ChevronLeft size={14} className="text-[#EF9F27]" /> Care
        </button>
      }
    >
      <div>
        <p className="text-[#A1A1AA]">Welcome back</p>
        <h1 className="font-head text-3xl font-extrabold text-[#F8FAFC]">Let's plan safely</h1>
      </div>

      {chip && (
        <div
          data-testid="profile-chip"
          className="mt-4 inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-4 py-2 text-sm font-semibold"
        >
          <span className="w-2 h-2 rounded-full bg-[#EF9F27]" />
          {chip}
        </div>
      )}

      {reminder && (
        <button
          data-testid="trip-reminder"
          onClick={() => navigate(`/trips/${reminder.id}/report`)}
          className="mt-6 w-full text-left bg-[#1A1A1A] text-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(12,27,51,0.2)]"
        >
          <div className="flex items-center gap-2 text-[#EF9F27] text-sm font-semibold">
            <Bell size={16} /> Upcoming trip
          </div>
          <h3 className="font-head text-lg font-bold mt-1">
            {reminder.destination_city} —{" "}
            {reminder.days_until_start === 0
              ? "starts today"
              : `in ${reminder.days_until_start} day${reminder.days_until_start === 1 ? "" : "s"}`}
          </h3>
          {reminder.has_report ? (
            <div className="mt-3">
              <div className="flex justify-between text-sm text-white/80 mb-1">
                <span>Pre-travel checklist</span>
                <span>
                  {reminder.checklist_done}/{reminder.checklist_total}
                </span>
              </div>
              <div className="h-2 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-[#EF9F27]" style={{ width: `${pct(reminder)}%` }} />
              </div>
              <p className="text-sm text-white/80 mt-2">
                {reminder.checklist_total && reminder.checklist_done >= reminder.checklist_total
                  ? "Checklist complete — you're ready to go!"
                  : "Tap to finish your pre-travel checklist"}
              </p>
            </div>
          ) : (
            <p className="text-sm text-white/80 mt-2">
              Generate your Travel Confidence Report to start preparing.
            </p>
          )}
        </button>
      )}

      <button
        data-testid="plan-trip-card"
        onClick={() => navigate("/trips/new")}
        className="mt-4 w-full text-left bg-[#EF9F27] rounded-2xl p-6 text-white shadow-[0_4px_20px_rgba(29,158,117,0.35)]"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-head text-2xl font-bold">Plan a new trip</h2>
            <p className="text-white/90 mt-1">Get a personalised Travel Confidence Report</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <Plus size={26} />
          </div>
        </div>
      </button>

      <button
        data-testid="explore-accessibility-card"
        onClick={() => navigate("/accessibility")}
        className="mt-4 w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5 shadow-[0_4px_20px_rgba(12,27,51,0.06)] flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center">
            <AccessIcon size={22} className="text-[#EF9F27]" />
          </div>
          <div>
            <h3 className="font-head text-lg font-bold text-[#F8FAFC]">Accessibility explorer</h3>
            <p className="text-sm text-[#A1A1AA]">Transport, stays & places to eat</p>
          </div>
        </div>
        <ChevronRight className="text-[#666666]" />
      </button>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head text-xl font-bold text-[#F8FAFC]">Your trips</h2>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-24 bg-[#1A1A1A] rounded-2xl border border-[#333333] animate-pulse" />
            ))}
          </div>
        ) : trips.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-6 text-center">
            <MapPin className="mx-auto text-[#666666]" />
            <p className="mt-2 text-[#A1A1AA]">No trips yet. Plan your first trip to get started.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {trips.map((t) => (
              <li key={t.id}>
                <button
                  data-testid={`trip-item-${t.id}`}
                  onClick={() => navigate(`/trips/${t.id}/report`)}
                  className="w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5 shadow-[0_4px_20px_rgba(12,27,51,0.06)] flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-head text-lg font-bold text-[#F8FAFC]">
                      {t.destination_city}, {t.destination_country}
                    </h3>
                    <p className="text-sm text-[#A1A1AA]">
                      {t.start_date} → {t.end_date}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      {t.has_report ? (
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#EF9F27]">
                          <FileText size={15} /> Report ready
                        </span>
                      ) : (
                        <span className="text-sm font-semibold text-amber-600">Report not generated</span>
                      )}
                      {t.report_is_demo && <DemoBadge />}
                    </div>
                  </div>
                  <ChevronRight className="text-[#666666] shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 bg-[#1A1A1A] rounded-2xl p-4">
        <p className="text-sm text-[#F8FAFC]" data-testid="home-info-card">
          {DISCLAIMER_SHORT}
        </p>
      </div>
    </AppShell>
  );
}
